import { FC, useCallback, useEffect, useMemo } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FieldProps } from "capitalrx-components-2.0/lib/forms/FileUpload/Field";
import { FileEntry } from "capitalrx-components-2.0/lib/forms/FileUpload/FileEntry";
import { useInputDisabled, useInputReadonly } from "capitalrx-components-2.0/lib/forms/InputGroup";
import { SubmitGroup } from "capitalrx-components-2.0/lib/forms/SubmitGroup";
import { ValidityGroup } from "capitalrx-components-2.0/lib/forms/ValidityGroup";

import { Input } from "./Input";

const TRANSLATION_PATH = "FILE_UPLOAD";

export const Field: FC<FieldProps> = (props) => {
  const { name, validator, required = false, defaultValue = null, maxSize } = props;

  const { t: translate } = useTranslation();

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const validateHandler = useCallback((value: ReadonlyArray<FileEntry> | null, formValues: FieldValues) => {
    if (required && (value === null || value === undefined || value.length === 0)) {
      return translate(`${TRANSLATION_PATH}.fileSelectionRequired`);
    }

    if (value && maxSize !== undefined && value.find((entry) => entry.file && entry.file.size > maxSize)) {
      return translate(`${TRANSLATION_PATH}.oneOrMoreFilesAreTooLarge`);
    }

    return validator ? validator(value, formValues) : true;
  }, [required, validator]);

  const rules = useMemo(() => ({ validate: disabled || readOnly ? undefined : validateHandler }), [validateHandler, disabled, readOnly]);

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({ name, defaultValue, rules });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input {...props} ref={ref} value={value} onChange={onChange} onFocusExit={onBlur} />
      </ValidityGroup>
    </SubmitGroup>
  );
};
